import type { NotificationTypeEnum } from '@maestro/commerce-blocks/components/notification/notification.types'
import type { UseCartDispatchProps } from '@maestro/commerce-blocks/hooks/hooks.types'

import { useCallback } from 'react'

import { emptyCartStructure } from '@maestro/commerce-blocks/components/cart/constants'
import {
  type Cart,
  type CartEntryStatus,
  type Product,
} from '@maestro/data-layer'

export const useCartDispatch = (props: UseCartDispatchProps) => {
  const { dispatch } = props

  const dispatchAddProduct = useCallback(
    (
      product: Product,
      variantId: string,
      notificationType: NotificationTypeEnum
    ) => {
      dispatch({
        type: 'add',
        product,
        variantId,
        notificationType,
      })
    },
    [dispatch]
  )

  const dispatchAddMultipleProducts = useCallback(
    (
      products: {
        product: Product
        variantId: string
        quantity: number
      }[],
      notificationType: NotificationTypeEnum
    ) => {
      dispatch({
        type: 'addMultiple',
        products,
        notificationType,
      })
    },
    [dispatch]
  )

  const dispatchUpdateCartEntry = useCallback(
    (
      cartEntryId: string,
      productId: string,
      variantId: string,
      quantity: number,
      notificationType: NotificationTypeEnum
    ) => {
      dispatch({
        type: 'update',
        cartEntryId,
        productId,
        variantId,
        quantity,
        notificationType,
      })
    },
    [dispatch]
  )

  const dispatchEmptyCart = useCallback(() => {
    dispatch({ type: 'empty' })
  }, [dispatch])

  const dispatchResetCart = useCallback(
    (cart: Cart = emptyCartStructure) => {
      dispatch({
        type: 'reset',
        cart,
      })
    },
    [dispatch]
  )

  const dispatchResolveCart = useCallback(
    (cart: Cart = emptyCartStructure) => {
      dispatch({
        type: 'resolve',
        cart,
      })
    },
    [dispatch]
  )

  const dispatchPushPending = useCallback(() => {
    dispatch({ type: 'push' })
  }, [dispatch])

  const dispatchAddCartEntryStatus = useCallback(
    (cartEntryStatus: CartEntryStatus) => {
      dispatch({
        type: 'pushCartEntryStatus',
        cartEntryStatus,
      })
    },
    [dispatch]
  )

  const dispatchEmptyCartEntryStatus = useCallback(() => {
    dispatch({ type: 'clearCartEntryStatus' })
  }, [dispatch])

  return {
    dispatchAddProduct,
    dispatchAddMultipleProducts,
    dispatchUpdateCartEntry,
    dispatchEmptyCart,
    dispatchResetCart,
    dispatchResolveCart,
    dispatchPushPending,
    dispatchAddCartEntryStatus,
    dispatchEmptyCartEntryStatus,
  }
}
